import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from "react";
import { genericEntityConstants } from "../../../api/GenericConstants";
import { Modal } from "../../../ELN/common/ELNModal/ELNModal";
import { GenericEntityDetailTable, GenericEntityDetailTableProps } from "./GenericEntityDetailTable";
import { Container } from "../../../common/panels/Container/Container";
import { toUppercase } from "../../../common/helperfunctions/stringFunctions";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { NotAllowed, NotAvailable } from "../../../common/misc/UIconstants";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";

export interface GenericEntityPreviewModalProps<ComponentProps extends GenericEntityDetailTableProps = {}> {
  triggerComponent?: ({ setIsOpen }: { setIsOpen: Dispatch<SetStateAction<boolean>> }) => JSX.Element;
  triggerComponentContainerStyle?: React.CSSProperties;
  btnLabel?: ReactNode | string;
  forceOpen?: boolean;
  onCloseCallback?: () => void;
  controls?: ReactNode;
  titleOverride?: string;
  detailTableProps?: ComponentProps;
  hideId?: boolean;
}

export const GenericEntityPreviewModal = <
  Entity extends GenericEntity,
  ComponentProps extends GenericEntityDetailTableProps = {}
>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, GenericEntityPreviewModalProps<ComponentProps>>) => {
  const { entityConstants } = genericEntityConstants[entityTypeId];

  const [isOpen, setIsOpen] = useState<boolean>(componentProps?.forceOpen ?? false);

  useEffect(() => {}, [isOpen]);
  const onClose = useCallback(() => {
    setIsOpen(false);
    componentProps?.onCloseCallback?.();
  }, [componentProps]);

  const { canRead } = useEntityPermissions({ entityTypeId });
  if (!entity) return null;
  if (!canRead(entity)) return NotAllowed;
  return (
    <>
      {!componentProps?.forceOpen && (
        <div
          className="flex align-center gap-5"
          style={{
            justifyContent: "space-between",
            paddingTop: "5px",
            ...componentProps?.triggerComponentContainerStyle,
          }}
        >
          {componentProps?.triggerComponent?.({ setIsOpen }) ?? (
            <button className="btn btn-primary flex align-center gap-5" onClick={() => setIsOpen(true)}>
              <LucideIcon name="maximize" />
              Show {entityConstants.entitySingular}
            </button>
          )}
        </div>
      )}
      <Modal isOpen={isOpen} onClose={onClose}>
        <Modal.Body style={{ padding: "5px" }}>
          <Container
            title={
              <div className="container_label">
                <div className="container_label_name flex align-center gap-5" style={{ fontWeight: 600 }}>
                  <LucideIcon name={entityConstants.icon} color="var(--primary)" />
                  {componentProps?.titleOverride ?? entity?.name ?? toUppercase(entityConstants.entitySingular)}
                </div>
                {!componentProps?.hideId && (
                  <div className={"container_label_id"}>
                    <span style={{ whiteSpace: "nowrap" }}>
                      {toUppercase(entityConstants.entitySingular)}-ID: {entity?.id ?? NotAvailable}
                    </span>
                  </div>
                )}
              </div>
            }
            containerStyle={{ borderRadius: "5px" }}
            style={{ maxHeight: "80vh", minWidth: "50vw", overflow: "auto" }}
            controls
          >
            <div style={{ width: "100%", height: "100%" }}>
              <GenericEntityDetailTable<Entity, ComponentProps>
                entity={entity}
                entityTypeId={entityTypeId}
                componentProps={componentProps?.detailTableProps}
              />
            </div>
          </Container>
        </Modal.Body>
        {!!componentProps?.controls && <Modal.Controls> {componentProps?.controls} </Modal.Controls>}
      </Modal>
    </>
  );
};
